import { useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import useFilterManagerWithState from '@/components/FilterManager/useFilterManagerWithState';
import useUsers from '@/components/UsersSearch/hooks/useUsers';
import {
  LearnerPageFilters,
  LearnerStatusFilters,
} from '@/modules/corporate/modules/learners/views/LearnerManager/types';
import {
  getLearnersSortDefaultValue,
  getSortAndFilterItems,
} from '@/modules/corporate/modules/learners/views/LearnerManager/utils';
import { UserTypeEnum } from '@/types/schema';

type UseLearnersProps = {
  learnerIds: string[];
  importedUserGroupIds?: string[];
  removedUserIds?: string[];
};

export default function useSeeAllLearners(props: UseLearnersProps) {
  const { t } = useTranslation();
  const { learnerIds, importedUserGroupIds, removedUserIds } = props;
  const filterManager = useFilterManagerWithState({});
  const {
    state: { sort, filter, search },
  } = filterManager;

  const sortAndFilterItems = useMemo(
    () => getSortAndFilterItems(t, LearnerPageFilters.DEFAULT, LearnerStatusFilters.ACTIVE),
    [t],
  );
  const OR = importedUserGroupIds && { groupIds: importedUserGroupIds, userIds: learnerIds };
  const {
    data: learners,
    isFetching,
    totalCount = 0,
    fetchMore,
  } = useUsers({
    ...filter,
    sort: sort.length ? sort : getLearnersSortDefaultValue(LearnerStatusFilters.ACTIVE),
    ids: OR ? undefined : learnerIds,
    notInIds: removedUserIds,
    userGroupIds: importedUserGroupIds,
    OR,
    searchText: search,
    pause: !learnerIds.length && !OR,
    userTypes: [UserTypeEnum.Learner],
  });

  return {
    data: learners,
    isFetching,
    totalCount,
    sortAndFilterItems,
    filterManager,
    fetchMore,
  };
}
